import CustomUpload from "@/components/CustomUpload";
import { colorSchemaList } from "@/utils/colorSchemas";
import { tabProps } from "@/utils/globalPropsTypes";
import { Button, ColorPicker, Form, Input, InputNumber, Radio, Select, Space, Switch } from "antd";
import { useEffect } from "react";
import { AiOutlineMinusCircle, AiOutlinePlus } from "react-icons/ai";
import { IoReload } from "react-icons/io5";

function AppearanceTab({ form, editItem = {}, setEditItem }) {
  const { component, counterType, gradientBg, counterBg, dynamicBg, chartType, dialType, subArcs } = editItem;

  const normalMax = Form.useWatch("loadSummaryNormalMax", form) ?? 50;
  const warningMax = Form.useWatch("loadSummaryWarningMax", form) ?? 80;

  useEffect(() => {
    if (!gradientBg) {
      // Reset counterBg to only one color when gradientBg is false
      form.setFieldsValue({
        counterBg: [{ color: Array.isArray(counterBg) && counterBg.length > 0 ? counterBg[0]?.color : "#fff" }],
      });
    }
  }, [gradientBg, form]);

  return (
    <>
      {/* color scheme */}
      {["bar", "line", "pie", "table", "comboBar"].includes(component) && (
        <>
          <Form.Item label="Select Color Scheme" name="colorSchema">
            <Select
              placeholder="Select Color Scheme"
              options={colorSchemaList[colorSchemaList[component]?.length ? component : "others"]?.map(
                ({ label, value, colors }) => ({
                  label: (
                    <span className="d-flex align-items-center">
                      {colors?.length
                        ? colors?.map((color, ind) => (
                          <span key={ind} className="colorPalateBox" style={{ background: color }}></span>
                        ))
                        : ""}
                      <span className="d-inline-block ms-3"> {label} </span>
                    </span>
                  ),
                  value,
                }),
              )}
            />
          </Form.Item>

          {chartType === "chartJs" && (
            <Form.Item label="Tick Gaps" name="tickGaps">
              <Input type="number" placeholder="Input Tick Gaps" className="w-100" />
            </Form.Item>
          )}
        </>
      )}

      {/* legend position */}
      {["bar", "line", "pie", "comboBar"].includes(component) && (
        <Form.Item name="legendPosition" initialValue="center bottom" label="Select Legend Position">
          <Select
            placeholder="Select Legend Position"
            options={[
              { label: "Left Top", value: "left top" },
              // { label: "Left Center", value: "left center" },
              { label: "Left Bottom", value: "left bottom" },
              { label: "Center Top", value: "center top" },
              { label: "Center Bottom", value: "center bottom" },
              { label: "Right Top", value: "right top" },
              // { label: "Right Center", value: "right center" },
              { label: "Right Bottom", value: "right bottom" },
              { label: "Hide", value: "hide" },
            ]}
          />
        </Form.Item>
      )}

      {/* bar line gird */}
      {["bar", "line", "comboBar"].includes(component) && (
        <>
          {chartType !== "chartJs" && (
            <Form.Item name="gridDashType" label="Grid Line Type">
              <Select
                placeholder="Select Grid Line Type"
                options={[
                  { label: "Solid", value: "solid" },
                  { label: "Dotted", value: "dot" },
                  { label: "Dashed", value: "dash" },
                ]}
              />
            </Form.Item>
          )}

          <Form.Item label="Select Date Format" name="dateFormat" initialValue="D/M/YY hh:mm a">
            <Radio.Group>
              <Radio value="D/M/YY hh:mm a">D/M/YY hh:mm a</Radio>
              <Radio value="DD MMM YYYY, hh:mm a">DD MMM YYYY, hh:mm a</Radio>
              <Radio value="DD/MM/YY">DD/MM/YY</Radio>
              <Radio value="DD/MMM/YYYY">DD/MMM/YYYY</Radio>
              <Radio value="HH:mm">hh:mm</Radio>
            </Radio.Group>
          </Form.Item>
        </>
      )}

      {["line"].includes(component) && (
        <>
          <Form.Item name="lineDashType" label="Graph Line Type">
            <Select
              placeholder="Select Graph Line Type"
              options={[
                { label: "Solid", value: "solid" },
                { label: "Dotted", value: "dot" },
                { label: "Dashed", value: "dash" },
              ]}
            />
          </Form.Item>

          <Form.Item name="markerType" label="Marker Type">
            <Select
              placeholder="Select Marker Type"
              options={[
                { label: "Circle", value: "circle" },
                { label: "Square", value: "square" },
                { label: "Triangle", value: "triangle" },
                { label: "Cross", value: "cross" },
                { label: "Hide", value: "none" },
              ]}
            />
          </Form.Item>

          <Form.Item label="Marker Color">
            <div className="d-flex gap-3">
              <Form.Item name="markerColor" getValueFromEvent={(_, color) => color}>
                <ColorPicker />
              </Form.Item>
              <Form.Item name="markerColor">
                <Input />
              </Form.Item>
              <Button
                type="button"
                icon={<IoReload />}
                onClick={() => {
                  setEditItem((pre) => ({ ...pre, markerColor: null }));
                  form.setFieldsValue({ markerColor: null });
                }}
              />
            </div>
          </Form.Item>
        </>
      )}

      {["pie"].includes(component) && (
        <>
          <Form.Item
            layout="horizontal"
            label="Doughnut Version "
            name="doughnut"
            colon={false}
            className="right-label mb-0"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            layout="horizontal"
            label="Show Percentage"
            name="showPercentage"
            colon={false}
            className="right-label mb-0"
          >
            <Switch />
          </Form.Item>

          <Form.Item layout="horizontal" label="Show Value" name="showValue" colon={false} className="right-label mb-0">
            <Switch />
          </Form.Item>
        </>
      )}

      {["counter"].includes(component) && (
        <>
          {counterType && counterType.includes("icon") && (
            <>
              <Form.Item label="Icon Color" initialValue="#1677ff">
                <div className="d-flex gap-3">
                  <Form.Item name="iconColor" getValueFromEvent={(_, color) => color}>
                    <ColorPicker />
                  </Form.Item>
                  <Form.Item name="iconColor">
                    <Input />
                  </Form.Item>
                  <Button
                    type="button"
                    icon={<IoReload />}
                    onClick={() => {
                      setEditItem((pre) => ({ ...pre, iconColor: "#1677ff" }));
                      form.setFieldsValue({ iconColor: "#1677ff" });
                    }}
                  />
                </div>
              </Form.Item>

              <Form.Item label="Icon Background Color" initialValue="#1677ff31">
                <div className="d-flex gap-3">
                  <Form.Item name="iconBgColor" getValueFromEvent={(_, color) => color}>
                    <ColorPicker />
                  </Form.Item>
                  <Form.Item name="iconBgColor">
                    <Input />
                  </Form.Item>
                  <Button
                    type="button"
                    icon={<IoReload />}
                    onClick={() => {
                      setEditItem((pre) => ({ ...pre, iconBgColor: "#1677ff31" }));
                      form.setFieldsValue({ iconBgColor: "#1677ff31" });
                    }}
                  />
                </div>
              </Form.Item>
            </>
          )}

          <Form.Item label="Value Color" initialValue="#000">
            <div className="d-flex gap-3">
              <Form.Item name="valueColor" getValueFromEvent={(_, color) => color}>
                <ColorPicker />
              </Form.Item>
              <Form.Item name="valueColor">
                <Input />
              </Form.Item>
              <Button
                type="button"
                icon={<IoReload />}
                onClick={() => {
                  setEditItem((pre) => ({ ...pre, valueColor: "#000" }));
                  form.setFieldsValue({ valueColor: "#000" });
                }}
              />
            </div>
          </Form.Item>

          {dynamicBg ? (
            <>
              <Form.Item label="Background Color: ">
                <Form.Item label="Default Color" initialValue="#000">
                  <div className="d-flex gap-3">
                    <Form.Item name="defaultCounterBg" getValueFromEvent={(_, color) => color}>
                      <ColorPicker />
                    </Form.Item>
                    <Form.Item name="defaultCounterBg">
                      <Input />
                    </Form.Item>
                    <Button
                      type="button"
                      icon={<IoReload />}
                      onClick={() => {
                        setEditItem((pre) => ({ ...pre, defaultCounterBg: "#000" }));
                        form.setFieldsValue({ defaultCounterBg: "#000" });
                      }}
                    />
                  </div>
                </Form.Item>

                <Form.List name="counterBgLevels" initialValue={[{ level: "0", color: "#fff" }]}>
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <Space key={key} style={{ display: "flex", marginBottom: 8 }} align="center">
                          <div className="d-flex gap-3 align-items-center">
                            <div className="d-flex gap-3">
                              <Form.Item {...restField} name={[name, "color"]} getValueFromEvent={(_, color) => color}>
                                <ColorPicker />
                              </Form.Item>
                              <Form.Item {...restField} name={[name, "color"]}>
                                <Input placeholder="Color" />
                              </Form.Item>
                            </div>
                            <Form.Item {...restField} name={[name, "level"]}>
                              <InputNumber placeholder="Level" className="w-100" />
                            </Form.Item>
                          </div>
                          <div style={{ marginBottom: "15px" }}>
                            <AiOutlineMinusCircle onClick={() => remove(name)} />
                          </div>
                        </Space>
                      ))}
                      <Form.Item>
                        <Button type="dashed" onClick={() => add()} block icon={<AiOutlinePlus />}>
                          Add Level
                        </Button>
                      </Form.Item>
                    </>
                  )}
                </Form.List>
              </Form.Item>
            </>
          ) : (
            <>
              <Form.Item
                layout="horizontal"
                label="Gradient Background"
                name="gradientBg"
                colon={false}
                className="right-label mb-0"
              >
                <Switch />
              </Form.Item>

              <Form.List name="counterBg" initialValue={[{ color: "#fff" }]}>
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name }, i, array) => (
                      <Space key={key} style={{ display: "flex", marginBottom: 8 }} align="center">
                        <div className="d-flex gap-3 align-items-center">
                          <Form.Item label={`Background Color ${array?.length > 1 ? i + 1 : ""}`}>
                            <div className="d-flex gap-3">
                              <Form.Item name={[name, "color"]} getValueFromEvent={(_, color) => color}>
                                <ColorPicker />
                              </Form.Item>
                              <Form.Item name={[name, "color"]}>
                                <Input placeholder="Color" />
                              </Form.Item>

                              {/* fixed this rest button */}
                              <Button
                                type="button"
                                icon={<IoReload />}
                                onClick={() => {
                                  const resetColor = "#fff";

                                  // 1. Update form field for that specific counterBg item
                                  form.setFieldValue(["counterBg", name, "color"], resetColor);

                                  // 2. Optionally update local state (editData or editItem)
                                  setEditItem((prev) => {
                                    const updated = { ...prev };
                                    if (updated.counterBg && Array.isArray(updated.counterBg)) {
                                      updated.counterBg = [...updated.counterBg]; // clone the array
                                      updated.counterBg[name] = {
                                        ...(updated.counterBg[name] || {}),
                                        color: resetColor,
                                      };
                                    }
                                    return updated;
                                  });
                                }}
                              />
                            </div>
                          </Form.Item>
                        </div>
                        {gradientBg && counterBg.length > 1 && (
                          <div style={{ marginBottom: "5px" }}>
                            <AiOutlineMinusCircle onClick={() => remove(name)} />
                          </div>
                        )}
                      </Space>
                    ))}
                    {gradientBg && (
                      <Form.Item>
                        <Button
                          type="dashed"
                          disabled={counterBg?.length >= 5}
                          onClick={() => add()}
                          block
                          icon={<AiOutlinePlus />}
                        >
                          Add Colors
                        </Button>
                      </Form.Item>
                    )}
                  </>
                )}
              </Form.List>
            </>
          )}
        </>
      )}

      {["textBox", "html"].includes(component) && (
        <>
          <Form.Item label="Background Color" initialValue="#fff">
            <div className="d-flex gap-3">
              <Form.Item name="bgColor" getValueFromEvent={(_, color) => color}>
                <ColorPicker />
              </Form.Item>
              <Form.Item name="bgColor">
                <Input />
              </Form.Item>
              <Button
                type="button"
                icon={<IoReload />}
                onClick={() => {
                  setEditItem((pre) => ({ ...pre, bgColor: "#fff" }));
                  form.setFieldsValue({ bgColor: "#fff" });
                }}
              />
            </div>
          </Form.Item>
        </>
      )}

      {["splitCounter"].includes(component) && (
        <>
          <Form.Item label="Title Background Color" initialValue="#00B1A9">
            <div className="d-flex gap-3">
              <Form.Item name="bgColor" getValueFromEvent={(_, color) => color}>
                <ColorPicker />
              </Form.Item>
              <Form.Item name="bgColor">
                <Input />
              </Form.Item>
              <Button
                type="button"
                icon={<IoReload />}
                onClick={() => {
                  setEditItem((pre) => ({ ...pre, bgColor: "#00B1A9" }));
                  form.setFieldsValue({ bgColor: "#00B1A9" });
                }}
              />
            </div>
          </Form.Item>
          <Form.Item label="Value Background Color" initialValue="#A6A6A6">
            <div className="d-flex gap-3">
              <Form.Item name="bgColor2" getValueFromEvent={(_, color) => color}>
                <ColorPicker />
              </Form.Item>
              <Form.Item name="bgColor2">
                <Input />
              </Form.Item>
              <Button
                type="button"
                icon={<IoReload />}
                onClick={() => {
                  setEditItem((pre) => ({ ...pre, bgColor2: "#A6A6A6" }));
                  form.setFieldsValue({ bgColor2: "#A6A6A6" });
                }}
              />
            </div>
          </Form.Item>

          <Form.Item label="Value Font Color" initialValue="#A6A6A6">
            <div className="d-flex gap-3">
              <Form.Item name="valueColor" getValueFromEvent={(_, color) => color}>
                <ColorPicker />
              </Form.Item>
              <Form.Item name="valueColor">
                <Input />
              </Form.Item>
              <Button
                type="button"
                icon={<IoReload />}
                onClick={() => {
                  setEditItem((pre) => ({ ...pre, valueColor: "#fff" }));
                  form.setFieldsValue({ valueColor: "#fff" });
                }}
              />
            </div>
          </Form.Item>
        </>
      )}

      {["dial"].includes(component) &&
        (["simple", "gradient", "temperature"].includes(dialType) ? (
          <Form.List
            label="Gauge Color Limit"
            name="subArcs"
            initialValue={[
              { color: "#00ff00", limit: 35, showTick: true },
              { color: "#FFFF00", limit: 70, showTick: true },
              { color: "#ff0000", limit: 100, showTick: true },
            ]}
          >
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space key={key} style={{ display: "flex", marginBottom: 8 }} align="center">
                    <div className="d-flex gap-3 align-items-center">
                      <div className="d-flex gap-3">
                        <Form.Item name={[name, "color"]} getValueFromEvent={(_, color) => color}>
                          <ColorPicker />
                        </Form.Item>
                        <Form.Item name={[name, "color"]}>
                          <Input placeholder="Color" />
                        </Form.Item>
                      </div>
                      <Form.Item {...restField} name={[name, "limit"]}>
                        <InputNumber min={0} max={100} placeholder="Limit" className="w-100" />
                      </Form.Item>
                    </div>
                    <div>
                      <Form.Item {...restField} name={[name, "showTick"]} initialValue={true} hidden>
                        <Input type="hidden" />
                      </Form.Item>
                    </div>
                    <div style={{ marginBottom: "15px" }}>
                      <Button disabled={!subArcs?.length || subArcs?.length <= 3} type="text">
                        <AiOutlineMinusCircle onClick={() => remove(name)} />
                      </Button>
                    </div>
                  </Space>
                ))}
                <Form.Item>
                  <Button
                    type="dashed"
                    disabled={subArcs?.length >= 10}
                    onClick={() => add({ color: "", limit: 0, showTick: true })}
                    block
                    icon={<AiOutlinePlus />}
                  >
                    Add Level
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        ) : (
          <>
            <Form.Item label="Start Color">
              <div className="d-flex gap-3">
                <Form.Item name="startColor" initialValue="#00ff00" getValueFromEvent={(_, color) => color}>
                  <ColorPicker />
                </Form.Item>
                <Form.Item name="startColor" initialValue="#00ff00">
                  <Input />
                </Form.Item>
                <Button
                  type="button"
                  icon={<IoReload />}
                  onClick={() => {
                    setEditItem((pre) => ({ ...pre, startColor: "#00ff00" }));
                    form.setFieldsValue({ startColor: "#00ff00" });
                  }}
                />
              </div>
            </Form.Item>
            <Form.Item label="End Color">
              <div className="d-flex gap-3">
                <Form.Item name="endColor" initialValue="#ff0000" getValueFromEvent={(_, color) => color}>
                  <ColorPicker />
                </Form.Item>
                <Form.Item name="endColor" initialValue="#ff0000">
                  <Input />
                </Form.Item>
                <Button
                  type="button"
                  icon={<IoReload />}
                  onClick={() => {
                    setEditItem((pre) => ({ ...pre, endColor: "#ff0000" }));
                    form.setFieldsValue({ endColor: "#ff0000" });
                  }}
                />
              </div>
            </Form.Item>
          </>
        ))}

      {/* {["table"].includes(component) && (
        <>
          <Form.Item name="tableLimit" label="Display Limit (Rows)">
            <InputNumber className="w-100" placeholder="Display Limit (Rows)" />
          </Form.Item>
        </>
      )} */}

      {["dynamicBox"].includes(component) && (
        <>
          <Form.Item label="Default Color" initialValue="#000">
            <div className="d-flex gap-3">
              <Form.Item name="dynamicBoxDefalutBG" getValueFromEvent={(_, color) => color}>
                <ColorPicker />
              </Form.Item>
              <Form.Item name="dynamicBoxDefalutBG">
                <Input />
              </Form.Item>
              <Button
                type="button"
                icon={<IoReload />}
                onClick={() => {
                  setEditItem((pre) => ({ ...pre, dynamicBoxDefalutBG: "#000" }));
                  form.setFieldsValue({ dynamicBoxDefalutBG: "#000" });
                }}
              />
            </div>
          </Form.Item>

          <Form.List name="dynamicBoxLevels" initialValue={[{ level: "0", color: "#fff" }]}>
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space key={key} style={{ display: "flex", marginBottom: 8 }} align="center">
                    <div className="d-flex gap-3 align-items-center">
                      <div className="d-flex gap-3">
                        <Form.Item name={[name, "color"]} getValueFromEvent={(_, color) => color}>
                          <ColorPicker />
                        </Form.Item>
                        <Form.Item name={[name, "color"]}>
                          <Input placeholder="Color" />
                        </Form.Item>
                      </div>
                      <Form.Item {...restField} name={[name, "level"]}>
                        <InputNumber placeholder="Level" className="w-100" />
                      </Form.Item>
                    </div>
                    <div style={{ marginBottom: "15px" }}>
                      <AiOutlineMinusCircle onClick={() => remove(name)} />
                    </div>
                  </Space>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block icon={<AiOutlinePlus />}>
                    Add Level
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </>
      )}

      {["loadSummary"].includes(component) && (
        <>
          <Form.Item label="Savings Baseline" name="loadSummaryBaseline" initialValue={0}>
            <InputNumber placeholder="Baseline Value" className="w-100" min={0} />
          </Form.Item>

          <Form.Item label={`Normal Level (0 - ${normalMax ?? ""})`}>
            <div className="d-flex gap-3 align-items-start">
              <Form.Item name="loadSummaryNormalColor" initialValue="#10b981" getValueFromEvent={(_, color) => color}>
                <ColorPicker />
              </Form.Item>
              <Form.Item name="loadSummaryNormalColor" initialValue="#10b981">
                <Input placeholder="Normal Color" />
              </Form.Item>
              <Form.Item
                name="loadSummaryNormalMax"
                initialValue={50}
                dependencies={["loadSummaryWarningMax"]}
                rules={[
                  { required: true, message: "Enter value" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const warning = getFieldValue("loadSummaryWarningMax");
                      if (value !== undefined && value !== null && warning !== undefined && warning !== null) {
                        if (Number(value) >= Number(warning)) {
                          return Promise.reject(new Error(`Must be less than Warning (${warning})`));
                        }
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <InputNumber min={0} placeholder="Max Value" style={{ width: "120px" }} />
              </Form.Item>
            </div>
          </Form.Item>

          <Form.Item label={`Warning Level (${normalMax ?? 0} - ${warningMax ?? ""})`}>
            <div className="d-flex gap-3 align-items-start">
              <Form.Item name="loadSummaryWarningColor" initialValue="#f59e0b" getValueFromEvent={(_, color) => color}>
                <ColorPicker />
              </Form.Item>
              <Form.Item name="loadSummaryWarningColor" initialValue="#f59e0b">
                <Input placeholder="Warning Color" />
              </Form.Item>
              <Form.Item
                name="loadSummaryWarningMax"
                initialValue={80}
                dependencies={["loadSummaryNormalMax"]}
                rules={[
                  { required: true, message: "Enter value" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const normal = getFieldValue("loadSummaryNormalMax");
                      if (value !== undefined && value !== null && normal !== undefined && normal !== null) {
                        if (Number(value) <= Number(normal)) {
                          return Promise.reject(new Error(`Must be greater than Normal (${normal})`));
                        }
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <InputNumber min={0} placeholder="Max Value" style={{ width: "120px" }} />
              </Form.Item>
            </div>
          </Form.Item>

          <Form.Item label={`Critical Level (> ${warningMax ?? ""})`}>
            <div className="d-flex gap-3 align-items-center">
              <Form.Item name="loadSummaryCriticalColor" initialValue="#ef4444" getValueFromEvent={(_, color) => color}>
                <ColorPicker />
              </Form.Item>
              <Form.Item name="loadSummaryCriticalColor" initialValue="#ef4444">
                <Input placeholder="Critical Color" />
              </Form.Item>
            </div>
          </Form.Item>
        </>
      )}

      {["image"].includes(component) ? (
        <CustomUpload
          {...{
            form,
            formItemsProps: { name: "image", label: "Upload Image" },
            maxSizeMB: 1,
            uploadProps: {
              // listType: "picture-circle",
              accept: "image/*",
              showUploadList: {
                showPreviewIcon: false,
                showRemoveIcon: true,
              },
            },
          }}
        />
      ) : (
        ""
      )}
    </>
  );
}

export default AppearanceTab;

AppearanceTab.propTypes = tabProps;
